import { useState, useCallback } from 'react'
import { api, ApiError, getDeviceId } from '../utils/api'
import { normalizeChoiceText } from '../utils/choiceNormalizer'
import logger from '../utils/logger'
import { trackChoiceAdded, trackErrorChoiceAdd } from '../utils/analytics'

/**
 * Custom hook for managing choice addition
 * @param {Object} poll - The poll object (contains id as UUID)
 * @param {Array} choices - Current choices array
 * @param {Function} setChoices - Function to update choices
 * @returns {Object} Hook state and functions
 */
function useChoiceManagement(poll, choices, setChoices) {
  // Use poll.id (UUID) directly to avoid short code lookup on backend
  const pollId = poll?.id

  const [newChoiceText, setNewChoiceText] = useState('')
  const [newChoicePrice, setNewChoicePrice] = useState(null)
  const [newChoiceOptions, setNewChoiceOptions] = useState([])
  const [addingChoice, setAddingChoice] = useState(false)
  const [addChoiceError, setAddChoiceError] = useState('')

  const handleAddChoice = useCallback(async (e) => {
    e.preventDefault()

    if (!pollId) {
      setAddChoiceError('Poll not loaded yet')
      return
    }

    if (!newChoiceText.trim()) {
      setAddChoiceError('Please enter choice text')
      return
    }

    if (newChoiceText.length > 100) {
      setAddChoiceError('Choice text must be 100 characters or less')
      return
    }

    // Check for duplicate choices (smart uniqueness validation)
    const normalizedNewText = normalizeChoiceText(newChoiceText.trim())
    if (normalizedNewText.length > 0) {
      const hasDuplicate = choices.some(choice =>
        normalizeChoiceText(choice.text) === normalizedNewText
      )

      if (hasDuplicate) {
        setAddChoiceError('A choice with similar text already exists')
        return
      }
    }

    setAddingChoice(true)
    setAddChoiceError('')

    try {
      logger.info('Adding choice to poll', { pollId, text: newChoiceText.substring(0, 50) + '...' })

      const deviceId = getDeviceId()
      const requestBody = {
        text: newChoiceText.trim(),
        device_id: deviceId
      }

      // Add price if provided
      if (newChoicePrice !== null && newChoicePrice !== undefined) {
        requestBody.price = newChoicePrice
      }

      // Add options if provided
      if (newChoiceOptions && newChoiceOptions.length > 0) {
        requestBody.options = newChoiceOptions
      }

      const response = await api.post(`/api/polls/${pollId}/choices`, requestBody)

      // Add the new choice to local state (with duplicate checking)
      setChoices(prev => {
        // Check if choice already exists (avoid duplicates from realtime)
        if (prev.some(choice => choice.id === response.id)) {
          logger.warn('Add choice: Choice already exists, skipping', { choiceId: response.id })
          return prev
        }

        const choiceWithCount = {
          ...response,
          vote_count: response.vote_count || 0,
          upvote_count: response.upvote_count || 0,
          downvote_count: response.downvote_count || 0
        }

        const updated = [...prev, choiceWithCount]

        // Sort by vote count DESC, then created_at ASC
        updated.sort((a, b) => {
          if (b.vote_count !== a.vote_count) return b.vote_count - a.vote_count
          return new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        })

        return updated
      })

      // Reset all fields
      setNewChoiceText('')
      setNewChoicePrice(null)
      setNewChoiceOptions([])

      logger.info('Choice added successfully', {
        pollId,
        choiceId: response.id,
        text: response.text.substring(0, 50) + '...',
        hasPrice: newChoicePrice !== null,
        hasOptions: newChoiceOptions.length > 0
      })
      trackChoiceAdded(pollId, newChoiceText.trim().length)

    } catch (err) {
      const errorMessage = err instanceof ApiError
        ? err.message
        : 'Failed to add choice. Please try again.'

      logger.error('Failed to add choice', { pollId, error: errorMessage })
      trackErrorChoiceAdd(pollId, errorMessage)
      setAddChoiceError(errorMessage)
    } finally {
      setAddingChoice(false)
    }
  }, [pollId, choices, setChoices, newChoiceText, newChoicePrice, newChoiceOptions])

  return {
    newChoiceText,
    setNewChoiceText,
    newChoicePrice,
    setNewChoicePrice,
    newChoiceOptions,
    setNewChoiceOptions,
    addingChoice,
    addChoiceError,
    setAddChoiceError,
    handleAddChoice
  }
}

export default useChoiceManagement

